import type { ParamsShape } from "@circle-react/helpers/profileHelpers";
import type { CommunityMember } from "@circle-react/types";
import { Avatar } from "@circleco/compass/components/Avatar";
import { Typography } from "@circleco/compass/components/Typography";
import type { ReactNode } from "react";

import { AcceptIgnoreButtons } from "@/react/components/ConnectionButtons/AcceptIgnoreButtons";
import { ConnectedBadge } from "@/react/components/ConnectionButtons/ConnectedBadge";
import { ConnectionButtonsOrchestrator } from "@/react/components/ConnectionButtons/ConnectionButtonsOrchestrator";
import { useConnectionButtonState } from "@/react/components/ConnectionButtons/hooks/useConnectionButtonState";
import { ConnectionNote } from "@/react/components/ProfileModalV3/Body/ConnectBanner/ConnectionNote";
import { useIsConnectPopoverPanel } from "@/react/components/shared/Connect/ConnectPopoverContext";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useProfileLinkProps } from "@/react/hooks/profile/useProfileLinkProps";

import { MetaRow } from "./MetaRow";

export interface CompassMemberRowProps {
  activeStateDisplay?: "badge" | "message";
  canReceiveDmFromCurrentMember?: boolean;
  className?: string;
  iconOnly?: boolean;
  member: CommunityMember;
  profileLinkParams?: ParamsShape;
  recommendationId?: number;
  showMomentumModal?: boolean;
  topRightExtra?: ReactNode;
}

export const MobileCompassMemberRow = ({
  activeStateDisplay = "message",
  canReceiveDmFromCurrentMember,
  className,
  member,
  profileLinkParams,
  recommendationId,
  showMomentumModal = false,
  topRightExtra,
  iconOnly = false,
}: CompassMemberRowProps) => {
  const profileLinkProps = useProfileLinkProps({
    public_uid: member.public_uid ?? "",
    params: profileLinkParams,
  });

  const {
    isPendingReceived: isOtherUserSentConnection,
    isActive: isConnected,
    connection: memberConnection,
  } = useConnectionButtonState(member);

  const isConnectPopoverPanel = useIsConnectPopoverPanel();

  const hasTopRightActions =
    isOtherUserSentConnection || Boolean(topRightExtra);

  return (
    <div className={classNames("flex gap-4", className)}>
      <a {...profileLinkProps} className="shrink-0">
        <Avatar name={member.name} src={member.avatar_url} size="lg" />
      </a>

      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex w-full items-start justify-between gap-2">
          <div className="flex w-full min-w-0 flex-col gap-2">
            <div className="flex w-full items-center justify-between gap-2">
              <a {...profileLinkProps} className="min-w-0 overflow-hidden">
                <Typography variant="label-sm" truncate>
                  {member.name}
                </Typography>
              </a>
              {isConnected && !isConnectPopoverPanel && (
                <ConnectedBadge
                  connectedAt={
                    memberConnection?.accepted_at ??
                    memberConnection?.created_at
                  }
                  size="sm"
                />
              )}
            </div>

            {member.headline && (
              <Typography variant="label-xs" color="secondary" truncate>
                {member.headline}
              </Typography>
            )}

            <MetaRow
              location={member.location}
              mutualCount={member.mutual_connections_count}
            />
          </div>

          {hasTopRightActions && (
            <div className="flex shrink-0 items-start gap-2">
              {isOtherUserSentConnection && (
                <AcceptIgnoreButtons
                  iconOnly={iconOnly}
                  recipient={member}
                  connectionId={memberConnection?.id?.toString()}
                  showMomentumModal={showMomentumModal}
                  acceptButtonPosition="right"
                />
              )}
              {topRightExtra}
            </div>
          )}
        </div>

        {isOtherUserSentConnection && memberConnection?.note && (
          <ConnectionNote
            note={memberConnection.note}
            connectionId={memberConnection.id?.toString()}
            initiatorIsAdmin={memberConnection.initiator?.admin}
            member={member}
            collapse
          />
        )}

        {!isOtherUserSentConnection && (
          <ConnectionButtonsOrchestrator
            activeStateDisplay={activeStateDisplay}
            acceptIgnoreIconOnly={iconOnly}
            canReceiveDmFromCurrentMember={canReceiveDmFromCurrentMember}
            member={member}
            recommendationId={recommendationId}
            showMomentumModal={showMomentumModal}
            size="sm"
          />
        )}
      </div>
    </div>
  );
};
